import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import "./ProductDetails.css";
import { AuthContext } from "../../routes/AuthContext";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { addToCart } from "../cart/Cart";
import { addToWishlist, removeFromWishlist } from "../wishlist/Wishlist";

const ProductDetails = () => {
  const navigate = useNavigate();

  const { id } = useParams(); // product_id from URL

  const [uoms, setUoms] = useState([]);
  const [selectedUomId, setSelectedUomId] = useState(null);

  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const { setCartCount } = useContext(AuthContext);
  const [cartQty, setCartQty] = useState(0);

  // Review States
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFormData, setReviewFormData] = useState({
    rating: 5,
    comments: "",
    images: [],
  });

  const unitPrice = Number(product?.selling_price || 0);
  const totalPrice = unitPrice * quantity;

  const [submittingReview, setSubmittingReview] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  /*  AUTH CONTEXT */
  const { getCustomerData, isAuthenticated } = useContext(AuthContext);
  const customer = getCustomerData();
  const customer_id = customer?.customer_id;

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      toast.warning("Please login to continue");
      return;
    }

    navigate("/payment", {
      state: {
        product,
        quantity,
        totalPrice,
        unitPrice,
        uom_id: selectedUomId || null,
      },
    });
  };

  /* =========================
     FETCH PRODUCT DETAILS
  ========================== */
  useEffect(() => {
    if (!id) return;

    const fetchProductDetails = async () => {
      try {
        setLoading(true);

        const res = await axios.post(`${BASE_URL}product_details`, {
          product_id: id,
          customer_id, // optional
        });

        if (res.data.api_success) {
          setProduct(res.data.product_details);
        }
      } catch (error) {
        console.error("Product details error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id, BASE_URL, customer_id]);

  /* =========================
   FETCH UNIT OF MEASUREMENTS
========================= */
  useEffect(() => {
    const fetchUoms = async () => {
      try {
        const res = await axios.post(`${BASE_URL}unit_of_measurements`);

        if (res.data.api_success) {
          setUoms(res.data.data || []);

          // Default select first UOM
        }
      } catch (error) {
        console.error("UOM fetch error:", error);
      }
    };

    fetchUoms();
  }, [BASE_URL]);

  /* =========================
     FETCH REVIEWS
  ========================== */
  useEffect(() => {
    if (!id) return;

    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        const formData = new FormData();
        formData.append("product_id", id);

        const res = await axios.post(
          `${BASE_URL}reviews_list_details`,
          formData,
        );

        if (res.data.api_success) {
          setReviews(res.data.data || []);
        }
      } catch (error) {
        console.error("Reviews fetch error:", error);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, [id, BASE_URL]);

  /* =========================
     CHECK IF PRODUCT IS WISHLISTED
  ========================== */
  useEffect(() => {
    if (!customer_id || !id) return;

    const checkWishlist = async () => {
      try {
        const formData = new FormData();
        formData.append("customer_id", customer_id);

        const res = await axios.post(
          `${BASE_URL}customer_wish_list_details`,
          formData,
        );

        if (res.data.api_success) {
          const wishlistIds = res.data.wishlist_items.map((item) =>
            String(item.product_id),
          );
          setIsWishlisted(wishlistIds.includes(String(id)));
        }
      } catch (error) {
        console.error("Wishlist check error:", error);
      }
    };

    checkWishlist();
  }, [id, customer_id, BASE_URL]);

  /* =========================
      TOGGLE WISHLIST
  ========================== */
  const toggleWishlist = async () => {
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to use wishlist");
      return;
    }

    try {
      if (isWishlisted) {
        const res = await removeFromWishlist(customer_id, id);

        if (res.api_success) {
          setIsWishlisted(false);
          toast.info("Removed from wishlist");
        } else {
          toast.error(res.message || "Failed to remove from wishlist");
        }
      } else {
        const res = await addToWishlist(customer_id, id);

        if (res.api_success) {
          setIsWishlisted(true);
          toast.success("Added to wishlist");
        } else {
          toast.error(res.message || "Failed to add to wishlist");
        }
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      toast.error("Something went wrong");
    }
  };

  /* =========================
    ADD TO CART 
  ========================== */

  useEffect(() => {
    if (!customer_id || !id) return;

    const fetchCartQty = async () => {
      try {
        const formData = new FormData();
        formData.append("customer_id", customer_id);

        const res = await axios.post(
          `${BASE_URL}customer_cart_details_list`,
          formData,
        );

        if (res.data.api_success) {
          const existingItem = res.data.cart_items.find(
            (item) => String(item.product_id) === String(id),
          );

          if (existingItem) {
            setCartQty(parseInt(existingItem.quantity, 10));
          } else {
            setCartQty(0);
          }
        }
      } catch (error) {
        console.error("Cart qty fetch error:", error);
      }
    };

    fetchCartQty();
  }, [customer_id, id, BASE_URL]);

  const handleAddToCart = async () => {
    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to add items to cart");
      return;
    }

    if (addingToCart) return;

    try {
      setAddingToCart(true);

      // SAME LOGIC AS PRODUCT CARD
      const isNewProduct = cartQty === 0;
      const newQty = cartQty + quantity;

      const res = await addToCart(
        customer_id,
        id,
        newQty,
        setCartCount,
        quantity,
        isNewProduct ? 1 : 0,
      );

      if (res.api_success) {
        setCartQty(newQty); // update local qty
        toast.success(cartQty === 0 ? "Added to cart" : "Quantity increased");
      } else {
        toast.error(res.message || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
      toast.error("Something went wrong");
    } finally {
      setTimeout(() => {
        setAddingToCart(false);
      }, 800);
    }
  };

  /* =========================
     SUBMIT REVIEW
  ========================== */
  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated || !customer_id) {
      toast.warning("Please login to submit a review");
      return;
    }

    if (!reviewFormData.comments.trim()) {
      toast.error("Please add a comment");
      return;
    }

    try {
      setSubmittingReview(true);
      const formData = new FormData();
      formData.append("customer_id", customer_id);
      formData.append("product_id", id);
      formData.append("rating", reviewFormData.rating);
      formData.append("comments", reviewFormData.comments);

      // Append images if any
      reviewFormData.images.forEach((image) => {
        formData.append(`review_images[]`, image);
      });

      const res = await axios.post(`${BASE_URL}reviews_submit`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.api_success) {
        toast.success("Review submitted successfully!");
        setReviewFormData({ rating: 5, comments: "", images: [] });
        setShowReviewForm(false);

        // Refresh reviews
        const formDataRefresh = new FormData();
        formDataRefresh.append("product_id", id);
        const reviewsRes = await axios.post(
          `${BASE_URL}reviews_list_details`,
          formDataRefresh,
        );
        if (reviewsRes.data.api_success) {
          setReviews(reviewsRes.data.data || []);
        }
      } else {
        toast.error(res.data.message || "Failed to submit review");
      }
    } catch (error) {
      console.error("Review submit error:", error);
      toast.error("Something went wrong");
    } finally {
      setSubmittingReview(false);
    }
  };

  /* =========================
     HANDLE IMAGE SELECTION
  ========================== */
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + reviewFormData.images.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }
    setReviewFormData({
      ...reviewFormData,
      images: [...reviewFormData.images, ...files],
    });
  };

  /* =========================
     REMOVE IMAGE
  ========================== */
  const removeImage = (index) => {
    const newImages = reviewFormData.images.filter((_, i) => i !== index);
    setReviewFormData({ ...reviewFormData, images: newImages });
  };

  /* =========================
     RENDER STARS
  ========================== */
  const renderStars = (rating, interactive = false, onRatingChange = null) => {
    return (
      <div className="stars-wrapper">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star-icon ${star <= rating ? "active" : ""} ${
              interactive ? "clickable" : ""
            }`}
            onClick={() =>
              interactive && onRatingChange && onRatingChange(star)
            }
          >
            <i className="bi bi-star-fill"></i>
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return <p style={{ textAlign: "center" }}>Loading...</p>;
  }

  if (!product) {
    return <p style={{ textAlign: "center" }}>Product not found</p>;
  }

  return (
    <div className="product-container">
      <div className="product-box">
        <div className="row g-0">
          {/* IMAGE SECTION */}
          <div className="col-lg-5">
            <div className="image-section">
              {/*  WISHLIST ICON */}
              <button
                className={`wishlist-icon ${isWishlisted ? "active" : ""}`}
                onClick={toggleWishlist}
              >
                <i className={`bi bi-heart${isWishlisted ? "-fill" : ""}`}></i>
              </button>

              <div className="image-wrapper">
                <div className="thumbnails-left">
                  {product.images?.map((img, index) => (
                    <div
                      key={index}
                      className={`thumb ${
                        selectedImage === index ? "active" : ""
                      }`}
                      onClick={() => setSelectedImage(index)}
                    >
                      <img src={img} alt="product" />
                    </div>
                  ))}
                </div>

                <div className="main-img">
                  <img
                    src={product.images?.[selectedImage]}
                    alt={product.product_name}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* DETAILS SECTION */}
          <div className="col-lg-7">
            <div className="details-section">
              <h1 className="title">{product.product_name}</h1>

              {product.rating && (
                <div className="rating-row">
                  <span className="rating-badge">
                    {product.rating} <i className="bi bi-star-fill"></i>
                  </span>
                </div>
              )}

              <div className="price-row">
                <span className="price">₹{totalPrice}</span>

                {quantity > 1 && (
                  <span className="unit-price">
                    (₹{unitPrice} × {quantity})
                  </span>
                )}
              </div>

              <p className="description">
                <span>
                  Product Description : {product.product_descriptions}
                </span>
              </p>

              <div className="info-box">
                <div className="info-item">
                  <i className="bi bi-box-seam"></i>
                  <span>Category: {product.product_category_name}</span>
                </div>

                <div className="info-item">
                  <i className="bi bi-tag"></i>
                  <span>Unit: {product.uom_name || "--"}</span>
                </div>

                <div className="info-item">
                  <i className="bi bi-shield-check"></i>
                  <span>Quality Guaranteed</span>
                </div>
              </div>

              {/* QUANTITY */}
              {/* QUANTITY + UOM */}
              <div className="quantity-row">
                <label>Quantity:</label>

                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  {/* Quantity Box */}
                  <div className="qty-box">
                    <button
                      onClick={() => quantity > 1 && setQuantity(quantity - 1)}
                    >
                      -
                    </button>
                    <span>{quantity}</span>
                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                  </div>

                  {/* UOM DROPDOWN */}
                  <select
                    className="uom-dropdown"
                    value={selectedUomId ?? ""}
                    onChange={(e) =>
                      setSelectedUomId(
                        e.target.value ? Number(e.target.value) : null,
                      )
                    }
                  >
                    <option value="">Select Unit </option>

                    {uoms.map((uom) => (
                      <option key={uom.uom_id} value={uom.uom_id}>
                        {uom.uom_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ACTION BUTTONS */}
              <div className="action-btns">
                <button
                  className="btn-add-cart"
                  onClick={handleAddToCart}
                  disabled={addingToCart}
                  style={{
                    opacity: addingToCart ? 0.6 : 1,
                    cursor: addingToCart ? "not-allowed" : "pointer",
                  }}
                >
                  <i className="bi bi-cart3"></i>
                  {addingToCart ? "Adding..." : "Add to Cart"}
                </button>

                <button className="btn-buy" onClick={handleBuyNow}>
                  Buy Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================================
          REVIEWS SECTION
      ================================ */}
      <div className="reviews-main-section">
        <div className="reviews-container">
          {/* Header */}
          <div className="reviews-head">
            <div>
              <h2>Customer Reviews</h2>
              <p className="review-count">
                {reviews.length} review{reviews.length !== 1 ? "s" : ""}
              </p>
            </div>
            {isAuthenticated && (
              <button
                className="write-review-btn"
                onClick={() => setShowReviewForm(!showReviewForm)}
              >
                {showReviewForm ? "Cancel" : "Write Review"}
              </button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="review-form-container">
              <form onSubmit={handleReviewSubmit}>
                <div className="rating-input">
                  <label>Rating</label>
                  {renderStars(reviewFormData.rating, true, (rating) =>
                    setReviewFormData({ ...reviewFormData, rating }),
                  )}
                </div>

                <div className="comment-input">
                  <label>Your Review</label>
                  <textarea
                    placeholder="Share your experience with this product..."
                    value={reviewFormData.comments}
                    onChange={(e) =>
                      setReviewFormData({
                        ...reviewFormData,
                        comments: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="photo-input">
                  <label>Add Photos (Max 5)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    disabled={reviewFormData.images.length >= 5}
                  />
                  {reviewFormData.images.length > 0 && (
                    <div className="preview-grid">
                      {reviewFormData.images.map((img, idx) => (
                        <div key={idx} className="preview-item">
                          <img
                            src={URL.createObjectURL(img)}
                            alt={`Preview ${idx + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(idx)}
                            className="remove-preview"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="submit-review-btn"
                  disabled={submittingReview}
                >
                  {submittingReview ? "Submitting..." : "Submit Review"}
                </button>
              </form>
            </div>
          )}

          {/* Reviews List */}
          <div className="reviews-listing">
            {reviewsLoading ? (
              <div className="loading-state">Loading reviews...</div>
            ) : reviews.length > 0 ? (
              reviews.map((review, idx) => (
                <div key={idx} className="review-item">
                  <div className="review-top">
                    <div className="reviewer">
                      <div className="avatar">
                        {review.first_name?.charAt(0).toUpperCase() || "?"}
                      </div>
                      <div className="reviewer-meta">
                        <h4>{review.first_name || "Anonymous"}</h4>
                        <span className="review-time">
                          {new Date(review.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )}
                        </span>
                      </div>
                    </div>
                    {renderStars(review.rating)}
                  </div>

                  <p className="review-text">{review.comments}</p>

                  {review.review_images && review.review_images.length > 0 && (
                    <div className="review-photos">
                      {review.review_images.map((img, imgIdx) => (
                        <img
                          key={imgIdx}
                          src={img}
                          alt={`Review ${imgIdx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-reviews">
                <i className="bi bi-chat-square-text"></i>
                <p>No reviews yet</p>
                <span>Be the first to share your thoughts!</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
